import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Database, Plug, ScrollText, ShieldAlert, Landmark, LogOut, Smartphone } from "lucide-react";
import { api } from "@/lib/api";
import { ReadAloudBar } from "@/components/ReadAloudBar";

export function usePortal(path) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const reload = useCallback(() => api.get(path).then((r) => setData(r.data)).catch((e) => {
    if ([401, 403].includes(e?.response?.status)) nav("/admin", { replace: true });
  }), [path, nav]);
  useEffect(() => { reload(); }, [reload]);
  return [data, reload, setData];
}

const LINKS = [
  ["/portal", "Dashboard", LayoutDashboard, true], ["/portal/users", "Users & groups", Users], ["/portal/data", "Import / export", Database],
  ["/portal/integrations", "Integrations", Plug], ["/portal/audit", "Audit log", ScrollText], ["/portal/security", "Security tests", ShieldAlert],
  ["/portal/governance", "Governance", Landmark],
];

export default function PortalLayout() {
  const nav = useNavigate();
  const [state, setState] = useState(null);
  useEffect(() => {
    api.get("/admin/auth/state").then(({ data }) => (data.next !== "portal" ? nav("/admin", { replace: true }) : setState(data)));
  }, [nav]);
  const logout = async () => { await api.post("/admin/auth/logout"); nav("/admin"); };
  if (!state) return <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">Checking session…</div>;
  return (
    <div className="flex min-h-screen flex-col bg-background md:flex-row" dir="ltr" data-testid="portal-layout">
      <aside className="q1-serrated flex shrink-0 flex-col text-white md:sticky md:top-0 md:h-screen md:w-64">
        <div className="px-5 py-5">
          <p className="text-lg font-bold">Qatar One</p>
          <p className="text-[11px] text-white/70">Administrator portal · sandbox</p>
        </div>
        <nav className="q1-scroll flex gap-1 overflow-x-auto px-3 pb-3 md:flex-col md:overflow-visible">
          {LINKS.map(([to, label, Icon, end]) => (
            <NavLink key={to} to={to} end={end} data-testid={`portal-nav-${label.split(" ")[0].toLowerCase()}`}
              className={({ isActive }) => `flex min-h-[44px] shrink-0 items-center gap-2.5 rounded-xl px-3 text-sm font-medium transition-colors ${isActive ? "bg-white text-[#8A1538]" : "hover:bg-white/10"}`}>
              <Icon size={17} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto hidden space-y-1 px-3 pb-5 md:block">
          <p className="px-3 text-[11px] text-white/60" data-testid="portal-session-email">{state.email}</p>
          <NavLink to="/" className="flex min-h-[44px] items-center gap-2.5 rounded-xl px-3 text-sm hover:bg-white/10"><Smartphone size={17} /> Citizen app</NavLink>
          <button onClick={logout} data-testid="portal-logout-btn" className="flex min-h-[44px] w-full items-center gap-2.5 rounded-xl px-3 text-sm hover:bg-white/10"><LogOut size={17} /> Sign out</button>
        </div>
      </aside>
      <div className="min-w-0 flex-1">
        <ReadAloudBar />
        <div className="flex justify-end px-4 pt-3 md:hidden">
          <button onClick={logout} data-testid="portal-logout-btn-mobile" className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold"><LogOut size={14} /> Sign out</button>
        </div>
        <main className="mx-auto max-w-6xl p-4 md:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
